import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PerformancePageOverview } from 'api/new-analytics/types';
import ReportMetricCardsRow from './ReportMetricCardsRow';
import { getMetricDisplay } from 'app/modules/analytics/modules/performance/utils/getPerformanceMetricDisplay';
import { formatReportMetricLabel } from 'app/modules/analytics/modules/reports/utils/formatReportMetricLabel';

export interface ReportMetricRowDefinition {
  metricKey: string;
  sectionKind: 'performance' | 'content';
  tooltipKey?: string;
}

interface Props {
  rowDefinitions: ReportMetricRowDefinition[];
  selectedMetrics: string[];
  overview: PerformancePageOverview | undefined;
  getContentMetricValue?: (metricKey: string) => string;
  isLoading: boolean;
  maxColumns: number;
}

const GRID_COLS_CLASS: Record<number, string> = {
  1: 'tw-grid tw-grid-cols-1',
  2: 'tw-grid tw-grid-cols-2',
  3: 'tw-grid tw-grid-cols-3',
  4: 'tw-grid tw-grid-cols-4',
  5: 'tw-grid tw-grid-cols-5',
};

const ReportPlatformMetricRows = ({
  rowDefinitions,
  selectedMetrics,
  overview,
  getContentMetricValue,
  isLoading,
  maxColumns,
}: Props) => {
  const { t } = useTranslation();

  const items = useMemo(() => {
    const selected = new Set(selectedMetrics);

    return rowDefinitions
      .filter((definition) => selected.has(definition.metricKey))
      .map((definition) => {
        const value =
          definition.sectionKind === 'performance'
            ? getMetricDisplay(overview, definition.metricKey).value
            : getContentMetricValue?.(definition.metricKey) ?? '—';
        const badge =
          definition.sectionKind === 'performance'
            ? getMetricDisplay(overview, definition.metricKey).badge
            : undefined;

        return {
          title: formatReportMetricLabel(
            definition.metricKey,
            definition.sectionKind,
            t,
          ),
          value,
          badge,
        };
      });
  }, [getContentMetricValue, overview, rowDefinitions, selectedMetrics, t]);

  if (items.length === 0) {
    return null;
  }

  const effectiveCols = Math.min(2, maxColumns, items.length) || 1;

  return (
    <ReportMetricCardsRow
      isLoading={isLoading}
      skeletonCount={items.length}
      gridClassName={GRID_COLS_CLASS[effectiveCols] ?? GRID_COLS_CLASS[4]}
      items={items}
    />
  );
};

export default ReportPlatformMetricRows;
